import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, FileText, Users, FolderKanban, CheckSquare, Scale, Building2, GitPullRequest, Receipt, FileSignature, Paperclip, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const TYPE_ICONS = {
  project: <FolderKanban className="w-4 h-4 text-blue-500" />,
  employee: <Users className="w-4 h-4 text-emerald-500" />,
  task: <CheckSquare className="w-4 h-4 text-amber-500" />,
  legal_case: <Scale className="w-4 h-4 text-purple-500" />,
  document: <FileText className="w-4 h-4 text-slate-600" />,
  decision: <GitPullRequest className="w-4 h-4 text-orange-500" />,
  department: <Building2 className="w-4 h-4 text-teal-500" />,
  financial_doc: <Receipt className="w-4 h-4 text-green-600" />,
  contract: <FileSignature className="w-4 h-4 text-indigo-500" />,
  contract_doc: <Paperclip className="w-4 h-4 text-pink-500" />,
  page: <Navigation className="w-4 h-4 text-orange-500" />
};

const TYPE_LABELS = {
  project: 'Project', employee: 'Employee', task: 'Task',
  legal_case: 'Legal Suit', document: 'Document', decision: 'Decision',
  department: 'Department', financial_doc: 'Financial Doc', contract: 'Contract',
  contract_doc: 'Contract Doc', page: 'Go to'
};

const QUICK_PAGES = [
  { title: 'Partners', subtitle: 'Partner master — KYC, company links, login access', link: '/partners', type: 'page' },
  { title: 'Company Master', subtitle: 'Company master — GST, PAN, registered address', link: '/companies', type: 'page' },
  { title: 'Green Building Compliance', subtitle: 'IGBC, GRIHA, LEED certifications', link: '/green-building', type: 'page' },
  { title: 'Leave & Attendance', subtitle: 'Apply leave, view balance, approvals', link: '/leave-management', type: 'page' },
  { title: 'Payroll', subtitle: 'Salary structures, monthly payroll', link: '/payroll', type: 'page' },
  { title: 'My Tasks', subtitle: 'Personal task board, kanban view', link: '/my-tasks', type: 'page' },
  { title: 'Task Templates', subtitle: 'Reusable task templates', link: '/task-templates', type: 'page' },
  { title: 'Projects', subtitle: 'All project management', link: '/projects', type: 'page' },
  { title: 'Employees', subtitle: 'Employee directory', link: '/employees', type: 'page' },
  { title: 'Contracts', subtitle: 'Work orders, agreements', link: '/contracts', type: 'page' },
  { title: 'RERA Compliance', subtitle: 'RERA registration tracking', link: '/rera-compliance', type: 'page' },
  { title: 'NOC & Sanctions', subtitle: 'NOC and sanction documents', link: '/noc-sanctions', type: 'page' },
  { title: 'Sales Inventory', subtitle: 'Flat inventory and allotments', link: '/sales-inventory', type: 'page' },
  { title: 'Market Intelligence', subtitle: 'Market transaction tracker', link: '/market-intelligence', type: 'page' },
  { title: 'Competitor Tracker', subtitle: 'Competitor project comparison', link: '/competitors', type: 'page' },
  { title: 'Legal Suits', subtitle: 'Legal case management', link: '/legal-cases', type: 'page' },
  { title: 'Cash Flow', subtitle: 'Budget and cash flow tracking', link: '/cashflow', type: 'page' },
  { title: 'Compliance Tracker', subtitle: 'NOC/Sanction compliance matrix', link: '/compliance-tracker', type: 'page' },
  { title: 'Flat Changes', subtitle: 'Flat modification requests', link: '/flat-changes', type: 'page' },
  { title: 'Financial Documents', subtitle: 'Company financial documents', link: '/financial-documents', type: 'page' },
  { title: 'Departments', subtitle: 'Department management', link: '/departments', type: 'page' },
  { title: 'Roles', subtitle: 'Role management', link: '/roles', type: 'page' },
  { title: 'Redevelopment', subtitle: 'Redevelopment deal tracking', link: '/redevelopment', type: 'page' },
  { title: 'Site Photos', subtitle: 'Site photo albums', link: '/site-albums', type: 'page' },
  { title: 'Drawings', subtitle: 'Project drawings', link: '/project-drawings', type: 'page' },
];

const UniversalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const data = await api.universalSearch(q);
      // Also search quick navigation pages
      const qLower = q.toLowerCase();
      const pageMatches = QUICK_PAGES.filter(p =>
        p.title.toLowerCase().includes(qLower) || p.subtitle.toLowerCase().includes(qLower)
      );
      setResults([...pageMatches, ...(data.results || [])]);
    } catch (e) {
      // Still show page navigation even if API fails
      const qLower = q.toLowerCase();
      const pageMatches = QUICK_PAGES.filter(p =>
        p.title.toLowerCase().includes(qLower) || p.subtitle.toLowerCase().includes(qLower)
      );
      setResults(pageMatches);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setResults([]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (result) => {
    navigate(result.link);
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  return (
    <>
      {/* Search Trigger Button */}
      <button
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 100); }}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-slate-400 transition-colors border border-slate-600"
        data-testid="search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="hidden md:inline text-[10px] bg-slate-700 px-1.5 py-0.5 rounded font-mono">Ctrl+K</kbd>
      </button>

      {/* Search Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-[15vh]" data-testid="search-modal">
          <div ref={wrapperRef} className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
              <Search className="w-5 h-5 text-slate-600 flex-shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search projects, tasks, employees, documents, contracts, financial docs..."
                className="flex-1 text-sm outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
                autoFocus
                data-testid="search-input"
              />
              {query && (
                <button onClick={() => { setQuery(''); setResults([]); }}>
                  <X className="w-4 h-4 text-slate-600" />
                </button>
              )}
              <kbd className="text-[10px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">ESC</kbd>
            </div>

            {/* Results */}
            <div className="max-h-[50vh] overflow-y-auto">
              {loading && (
                <div className="p-4 text-center text-sm text-slate-600">Searching...</div>
              )}
              {!loading && query.length >= 2 && results.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-600">No results found for "{query}"</div>
              )}
              {!loading && results.length > 0 && (
                <div className="py-2">
                  {results.map((r, i) => (
                    <button
                      key={`${r.type}-${r.id}-${i}`}
                      onClick={() => handleSelect(r)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                      data-testid={`search-result-${i}`}
                    >
                      <div className="flex-shrink-0">{TYPE_ICONS[r.type] || <FileText className="w-4 h-4" />}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{r.title}</p>
                        <p className="text-xs text-slate-600 truncate">{r.subtitle}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-slate-600 bg-slate-100 px-2 py-0.5 rounded flex-shrink-0">
                        {TYPE_LABELS[r.type] || r.type}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {!loading && query.length < 2 && (
                <div className="p-6 text-center text-sm text-slate-600">
                  <p>Type at least 2 characters to search</p>
                  <p className="text-xs mt-2">Search across projects, tasks, employees, documents, contracts, financial docs, legal suits, and more</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UniversalSearch;
